import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, User, Mail, Phone, Bot } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useTranslation } from 'react-i18next';

const CRM_API = process.env.REACT_APP_CRM_API_URL || process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_CRM_API_KEY || '';
const LOCAL_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// AI response timeout - respond quickly for better UX
const AI_TIMEOUT = 500; // 0.5 seconds - quick response

const ChatWidget = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [visitor, setVisitor] = useState({ name: '', email: '', phone: '' });
  const [startingChat, setStartingChat] = useState(false);
  const [isBusinessHours, setIsBusinessHours] = useState(true);
  const [waitingForAgent, setWaitingForAgent] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastMessageTimeRef = useRef(null);
  const agentTimeoutRef = useRef(null);
  const lastVisitorMessageRef = useRef(null);

  // Auto-open chat after 5 seconds (desktop only)
  useEffect(() => {
    const savedConversation = localStorage.getItem('chat_conversation');
    if (savedConversation) {
      try {
        const data = JSON.parse(savedConversation);
        if (data.conversationId && data.visitor) {
          setConversationId(data.conversationId);
          setVisitor(data.visitor);
          setShowForm(false);
          fetchMessages(data.conversationId);
        }
      } catch (e) {
        // Invalid saved data, clear it
        localStorage.removeItem('chat_conversation');
      }
    }

    // Check business hours
    checkBusinessHours();

    // Only auto-open on desktop (screen width > 768px)
    const isMobile = window.innerWidth < 768;
    const timer = setTimeout(() => {
      if (!isOpen && !localStorage.getItem('chat_dismissed') && !isMobile) {
        setIsOpen(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Check business hours
  const checkBusinessHours = async () => {
    try {
      const response = await fetch(`${LOCAL_API}/chatbot/check-hours`);
      if (response.ok) {
        const data = await response.json();
        setIsBusinessHours(data.is_open);
      }
    } catch (error) {
      console.error('Error checking business hours:', error);
    }
  };

  // Poll for new messages when chat is open and conversation exists
  useEffect(() => {
    if (isOpen && conversationId) {
      pollIntervalRef.current = setInterval(() => {
        pollNewMessages();
      }, 3000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, conversationId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async (convId) => {
    try {
      const response = await fetch(`${CRM_API}/api/chat/messages/${convId}`, {
        headers: { 'X-Chat-API-Key': API_KEY }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        if (data.length > 0) {
          lastMessageTimeRef.current = data[data.length - 1].created_at;
        }
      } else if (response.status === 404) {
        // Conversation not found - clear saved data and show form
        localStorage.removeItem('chat_conversation');
        setConversationId(null);
        setShowForm(true);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      // On error, clear saved conversation to prevent repeated failures
      localStorage.removeItem('chat_conversation');
      setConversationId(null);
      setShowForm(true);
    }
  };

  const pollNewMessages = async () => {
    if (!conversationId || !lastMessageTimeRef.current) return;
    
    try {
      const response = await fetch(
        `${CRM_API}/api/chat/conversations/${conversationId}/new-messages?since=${encodeURIComponent(lastMessageTimeRef.current)}`,
        { headers: { 'X-Chat-API-Key': API_KEY } }
      );
      if (response.ok) {
        const newMessages = await response.json();
        if (newMessages.length > 0) {
          // Check if there's an agent response
          const agentMessages = newMessages.filter(m => m.sender_type === 'agent');
          if (agentMessages.length > 0) {
            // Agent responded, cancel AI timeout
            if (agentTimeoutRef.current) {
              clearTimeout(agentTimeoutRef.current);
              agentTimeoutRef.current = null;
            }
            setWaitingForAgent(false);
          }
          
          setMessages(prev => [...prev, ...newMessages]);
          lastMessageTimeRef.current = newMessages[newMessages.length - 1].created_at;
        }
      } else if (response.status === 404) {
        // Conversation no longer exists - stop polling and reset
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        localStorage.removeItem('chat_conversation');
        setConversationId(null);
        setShowForm(true);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error polling messages:', error);
    }
  };

  // Get AI response
  const getAIResponse = async (userMessage) => {
    setAiTyping(true);
    
    // Build context from recent messages
    const recentMessages = messages.slice(-6).map(m => 
      `${m.sender_type === 'visitor' ? 'Client' : 'Agent'}: ${m.message}`
    ).join('\n');
    
    try {
      const response = await fetch(`${LOCAL_API}/chatbot/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId,
          visitor_name: visitor.name,
          visitor_email: visitor.email,
          visitor_phone: visitor.phone || null,
          context: recentMessages
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Add AI response to messages immediately
        const aiMessage = {
          message_id: `ai_${Date.now()}`,
          sender_type: 'agent',
          message: data.response,
          created_at: new Date().toISOString(),
          is_ai: true
        };
        setMessages(prev => [...prev, aiMessage]);
        setAiTyping(false);
        setWaitingForAgent(false);
        
        // Sync to CRM in background (non-blocking)
        fetch(`${CRM_API}/api/chat/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Chat-API-Key': API_KEY
          },
          body: JSON.stringify({
            conversation_id: conversationId,
            sender_type: 'agent',
            message: `[Sama] ${data.response}`
          })
        }).catch(e => console.error('Error syncing AI message to CRM:', e));
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      setAiTyping(false);
      setWaitingForAgent(false);
    }
  };

  // Start AI timeout when visitor sends message
  const startAgentTimeout = (userMessage) => {
    // Clear any existing timeout
    if (agentTimeoutRef.current) {
      clearTimeout(agentTimeoutRef.current);
    }
    
    setWaitingForAgent(true);
    lastVisitorMessageRef.current = userMessage;
    
    // If outside business hours, respond immediately with AI
    if (!isBusinessHours) {
      getAIResponse(userMessage);
      return;
    }
    
    // During business hours, wait a short time then respond with AI
    agentTimeoutRef.current = setTimeout(() => {
      getAIResponse(userMessage);
    }, AI_TIMEOUT);
  };

  const startConversation = async (e) => {
    e.preventDefault();
    if (!visitor.name || !visitor.email) return;

    setStartingChat(true);
    try {
      // Generate a local conversation ID
      const localConversationId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Try CRM first, but don't fail if it doesn't work
      let crmConversationId = null;
      try {
        const response = await fetch(`${CRM_API}/api/chat/conversations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Chat-API-Key': API_KEY
          },
          body: JSON.stringify({
            visitor_name: visitor.name,
            visitor_email: visitor.email,
            visitor_phone: visitor.phone || null,
            source: 'website'
          })
        });

        if (response.ok) {
          const data = await response.json();
          crmConversationId = data.conversation_id;
        }
      } catch (crmError) {
        console.warn('CRM not available, using local chat:', crmError);
      }

      // Use CRM conversation ID if available, otherwise use local
      const conversationIdToUse = crmConversationId || localConversationId;
      
      setConversationId(conversationIdToUse);
      setShowForm(false);
      lastMessageTimeRef.current = new Date().toISOString();
      
      // Save to localStorage
      localStorage.setItem('chat_conversation', JSON.stringify({
        conversationId: conversationIdToUse,
        visitor,
        isLocal: !crmConversationId
      }));

      // Add welcome message
      setMessages([{
        message_id: 'welcome',
        sender_type: 'agent',
        message: t('chat.welcomeGreeting', { name: visitor.name }),
        created_at: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Error starting conversation:', error);
    } finally {
      setStartingChat(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistically add message
    const tempMessage = {
      message_id: `temp_${Date.now()}`,
      sender_type: 'visitor',
      message: messageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);

    // Try to send to CRM (non-blocking)
    try {
      fetch(`${CRM_API}/api/chat/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-API-Key': API_KEY
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          sender_type: 'visitor',
          message: messageText
        })
      }).catch(e => console.warn('CRM sync failed:', e));
    } catch (e) {
      console.warn('CRM not available');
    }

    // Update message time
    lastMessageTimeRef.current = new Date().toISOString();
    
    // Start timeout for AI response - this is the main functionality
    startAgentTimeout(messageText);
    setSending(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('chat_dismissed', 'true');
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('fr-CA', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed bottom-20 md:bottom-6 right-3 md:right-6 z-50" data-testid="chat-widget">
      {/* Chat Button - pill shape on mobile, round on desktop */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4 py-2.5 md:p-4 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 group"
          data-testid="chat-open-button"
        >
          <MessageCircle className="h-5 w-5 md:h-6 md:w-6" />
          <span className="text-sm font-medium md:hidden">Chat</span>
          <span className="hidden md:inline max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
            {t('chat.startConversation')}
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="bg-white rounded-2xl shadow-2xl w-[calc(100vw-32px)] sm:w-[340px] max-h-[70vh] sm:max-h-[550px] flex flex-col overflow-hidden border border-slate-200 animate-in slide-in-from-bottom-5 duration-300"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-[#3b82f6] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold">Entrepôt Auto Montréal</h3>
                <p className="text-xs text-blue-100">{t('chat.welcomeMessage')}</p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="hover:bg-white/20 rounded-full p-1 transition-colors"
              data-testid="chat-close-button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form or Messages */}
          {showForm ? (
            <form onSubmit={startConversation} className="p-4 space-y-4">
              <p className="text-sm text-slate-600 mb-4">
                {t('chat.greeting')} 👋 {t('chat.enterInfoPrompt')}
              </p>
              
              <div>
                <Label className="text-sm text-slate-700">{t('chat.nameLabel')} *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={visitor.name}
                    onChange={(e) => setVisitor(v => ({ ...v, name: e.target.value }))}
                    placeholder={t('chat.yourName')}
                    className="pl-10"
                    required
                    data-testid="chat-name-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-slate-700">{t('chat.emailLabel')} *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    value={visitor.email}
                    onChange={(e) => setVisitor(v => ({ ...v, email: e.target.value }))}
                    placeholder={t('chat.yourEmail')}
                    className="pl-10"
                    required
                    data-testid="chat-email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-slate-700">{t('chat.phoneLabel')}</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="tel"
                    value={visitor.phone}
                    onChange={(e) => setVisitor(v => ({ ...v, phone: e.target.value }))}
                    placeholder={t('chat.yourPhone')}
                    className="pl-10"
                    data-testid="chat-phone-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#3b82f6] hover:bg-blue-600"
                disabled={startingChat}
                data-testid="chat-start-button"
              >
                {startingChat ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {t('chat.startConversation')}
              </Button>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px] max-h-[350px] bg-slate-50">
                {messages.map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender_type === 'visitor' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        msg.sender_type === 'visitor'
                          ? 'bg-[#3b82f6] text-white rounded-br-md'
                          : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md shadow-sm'
                      }`}
                    >
                      {msg.is_ai && (
                        <div className="flex items-center gap-1 text-[10px] text-purple-600 mb-1">
                          <Bot className="h-3 w-3" />
                          <span>Sama</span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-[10px] mt-1 ${
                        msg.sender_type === 'visitor' ? 'text-blue-100' : 'text-slate-400'
                      }`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
                {/* AI Typing Indicator */}
                {aiTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white text-slate-800 border border-slate-200 rounded-2xl rounded-bl-md shadow-sm px-4 py-2">
                      <div className="flex items-center gap-1 text-[10px] text-purple-600 mb-1">
                        <Bot className="h-3 w-3" />
                        <span>Sama</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} className="p-3 border-t border-slate-200 bg-white">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('chat.typeMessage')}
                    className="flex-1"
                    disabled={sending || aiTyping}
                    data-testid="chat-message-input"
                  />
                  <Button 
                    type="submit" 
                    size="icon"
                    className="bg-[#3b82f6] hover:bg-blue-600"
                    disabled={sending || !newMessage.trim() || aiTyping}
                    data-testid="chat-send-button"
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
